// Unit conversion helpers (pure, DOM-free) for the print-oriented UI.
//
// The image data is inherently in pixels; physical size only exists through a
// print resolution (PPI). All internal math stays in pixels — this module is
// used ONLY at the display/input boundary of the web app.

export type Unit = 'px' | 'mm';

/**
 * Pixels → millimetres at a print resolution.
 * @param ppi pixels per inch; must be finite and > 0
 */
export function pxToMm(px: number, ppi: number): number {
  assertPpi(ppi);
  return (px * 25.4) / ppi;
}

/** Millimetres → pixels at a print resolution (inverse of pxToMm). */
export function mmToPx(mm: number, ppi: number): number {
  assertPpi(ppi);
  return (mm * ppi) / 25.4;
}

/** Throws on NaN/±Infinity/non-positive PPI (single source of truth). */
function assertPpi(ppi: number): void {
  if (!Number.isFinite(ppi) || ppi <= 0) {
    throw new Error(`invalid print resolution (ppi): ${ppi}`);
  }
}

/**
 * Format one or two lengths for display. px → integer pixels; mm → 1 decimal.
 * Example: formatDim(3000, 2000, 'mm', 150) → "508.0 × 338.7 mm"
 */
export function formatDim(w: number, h: number, unit: Unit, ppi: number): string {
  if (unit === 'mm') {
    return `${pxToMm(w, ppi).toFixed(1)} × ${pxToMm(h, ppi).toFixed(1)} mm`;
  }
  return `${Math.round(w)} × ${Math.round(h)} px`;
}
